import {
  DEFAULT_TIMEZONE,
  MULTI_ZONE_STATE_TIMEZONES,
  STATE_TIMEZONES,
  US_TIMEZONE_OPTIONS,
} from "@marble/core/modules/timezones/constants";
import type { SupportedTimezone } from "@marble/core/modules/timezones/constants";
import { State } from "@prisma/client";
import { DateTime } from "luxon";

export type ZonedWallClock = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  millisecond: number;
};

export function isSupportedTimezone(
  timezone: string,
): timezone is SupportedTimezone {
  return US_TIMEZONE_OPTIONS.some((option) => option.value === timezone);
}

/** A US state/territory's scheduling zone, per STATE_TIMEZONES — falls back
 * to DEFAULT_TIMEZONE for anything not in that best-effort mapping. */
export function resolveTimezoneForState(state: State): SupportedTimezone {
  return STATE_TIMEZONES[state] ?? DEFAULT_TIMEZONE;
}

/**
 * Every zone a client receiving care in `state` may be in, the default first.
 * One entry for all but the states in MULTI_ZONE_STATE_TIMEZONES — today, only
 * Arizona. This is the list an admin picks from when overriding a client's
 * zone, and the set a stored override is checked against.
 */
export function getTimezoneOptionsForState(
  state: State,
): readonly SupportedTimezone[] {
  return MULTI_ZONE_STATE_TIMEZONES[state] ?? [resolveTimezoneForState(state)];
}

/**
 * Display timezone for client-facing booking UIs (admin scheduling panel
 * for a specific therapist or round-robin, patient self-serve, and
 * onboarding). Prefers the insurance/"state of care" state, then the
 * client's home state — same precedence as
 * `resolveClientTimezone`. Falls back to DEFAULT_TIMEZONE when
 * neither is set.
 */
export function resolveClientTimezoneFromStates(
  insuranceState: State | null | undefined,
  homeState: State | null | undefined,
): SupportedTimezone {
  const stateOfCare = insuranceState ?? homeState;
  return stateOfCare ? resolveTimezoneForState(stateOfCare) : DEFAULT_TIMEZONE;
}

/**
 * Where a person is: their stored `User.timezone`, read off a record the
 * caller already holds.
 *
 * Works for any user type, which is why it isn't named for one — a
 * therapist, an admin, a school worker and the signed-in reader all keep
 * their zone in the same column and it means the same thing for each.
 * `resolveViewerTimezone` in the app is this function plus the lookup of who
 * is looking; scheduling calls it to anchor a provider's availability.
 *
 * Its counterpart is `resolveClientTimezoneFromUser`, the same read with a
 * different meaning: a client's column holds their zone *of care*, not where
 * they are. That is the distinction the two names carry — not staff-vs-client.
 *
 * Falls back to `DEFAULT_TIMEZONE` (Eastern) when the column is unset, or
 * holds a zone scheduling doesn't support.
 */
export function resolveUserTimezone(user: {
  timezone: string | null;
}): SupportedTimezone {
  const { timezone } = user;
  if (timezone != null && isSupportedTimezone(timezone)) {
    return timezone;
  }
  return DEFAULT_TIMEZONE;
}

/**
 * The client's clock for a call task: their stored `User.timezone` when the
 * caller has it, then `Student.state`, then the task's own `state`, then
 * Eastern.
 *
 * The column comes first because it is the only one of the three that can
 * hold a deliberate choice. Arizona is the case that matters: the Navajo
 * Nation observes DST and the rest of the state does not, so a family on
 * Mountain reads an hour off every summer if the state is all we look at.
 *
 * `storedTimezone` is a named field rather than a third positional argument
 * so a call site that cannot supply it has to leave the key out on purpose.
 * Several still do — they are working from a `UserTask` row that never
 * loaded the client's `User`. Those are correct-by-state, not correct.
 */
export function resolveAutodialerTimezone({
  storedTimezone,
  studentState,
  taskState,
}: {
  storedTimezone?: string | null;
  studentState?: State | null;
  taskState?: State | null;
}): SupportedTimezone {
  if (storedTimezone != null && isSupportedTimezone(storedTimezone)) {
    return storedTimezone;
  }
  const state = studentState ?? taskState;
  return state ? resolveTimezoneForState(state) : DEFAULT_TIMEZONE;
}

/** Wall-clock components of `date` in `timezone` (month is 1–12). */
export function getWallClockInTimezone(
  date: Date,
  timezone: string,
): ZonedWallClock {
  const dt = DateTime.fromJSDate(date, { zone: timezone });
  return {
    year: dt.year,
    month: dt.month,
    day: dt.day,
    hour: dt.hour,
    minute: dt.minute,
    second: dt.second,
    millisecond: dt.millisecond,
  };
}

/**
 * Interpret the given wall-clock as local time in `timezone` and return the
 * UTC instant. Month is 1–12.
 */
export function toUtcFromWallClock(
  wallClock: {
    year: number;
    month: number;
    day: number;
    hour?: number;
    minute?: number;
    second?: number;
    millisecond?: number;
  },
  timezone: string,
): Date {
  return DateTime.fromObject(
    {
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0,
      ...wallClock,
    },
    { zone: timezone },
  ).toJSDate();
}

/**
 * Keep `date`'s wall-clock in `timezone` and apply `patch` (month is 1–12).
 * When `date` is missing, starts from `fallback` (default now) in that zone.
 */
export function applyWallClockInTimezone(
  date: Date | undefined,
  timezone: string,
  patch: Partial<ZonedWallClock>,
  fallback: Date = new Date(),
): Date {
  const base = DateTime.fromJSDate(date ?? fallback, { zone: timezone });
  return base.set(patch).toJSDate();
}
